import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { textIn, type Lang } from '@rx-manager/forms';
import {
  FormApiError,
  formsApi,
  type FormPharmacy,
  type PublicFormInfo,
} from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { isEmail, isUsablePhone } from '../../lib/validate';
import { Alert, Button, Field, HEADING, Input, Spinner } from '../../components/ui';
import { DateOfBirth, EMPTY_DOB, isoFromParts, type DobParts } from './DateOfBirth';
import { FormStateMessage } from './FormStates';
import { rememberSession } from './form-session';
import { Turnstile } from './Turnstile';

type Ready = Extract<PublicFormInfo, { state: 'ready' }>;
type FieldName = 'firstName' | 'lastName' | 'dob' | 'contact';

/**
 * A pharmacy's poster or website form: `/f/p/<token>` (SFM-D9, D20).
 *
 * Anyone may open it, so the first step asks who they are: name, date of birth,
 * and a mobile number or an email. That is what lets staff match the answers to
 * a patient. The answers arrive unmatched and are never linked by themselves.
 * A hidden field catches simple bots; Turnstile, where the pharmacy has it, the
 * rest. The form itself then runs on its own link, like any other.
 */
export function PublicFormPage({
  publicToken,
  onPharmacy,
  onLanguages,
}: {
  publicToken: string;
  onPharmacy: (pharmacy: FormPharmacy) => void;
  onLanguages: (languages: Lang[]) => void;
}) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [info, setInfo] = useState<Ready | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable' | 'missing' | 'offline'>(
    'loading',
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState<DobParts>(EMPTY_DOB);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [turnstile, setTurnstile] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, MessageKey>>>({});
  const [refusal, setRefusal] = useState<MessageKey | null>(null);
  const [busy, setBusy] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const summary = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    formsApi
      .openPublic(publicToken)
      .then((result) => {
        if (cancelled) return;
        onPharmacy(result.pharmacy);
        if (result.state === 'unavailable') {
          setState('unavailable');
          return;
        }
        onLanguages(result.form.languages);
        setInfo(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(error instanceof FormApiError && error.status === 0 ? 'offline' : 'missing');
      });
    return () => {
      cancelled = true;
    };
  }, [publicToken, onPharmacy, onLanguages]);

  useEffect(() => {
    if (state === 'ready') heading.current?.focus();
  }, [state]);

  if (state === 'loading') return <Spinner label={t('form.loading')} />;
  if (state === 'offline') return <Alert>{t('error.offline')}</Alert>;
  if (state === 'missing' || state === 'unavailable' || !info) {
    return <FormStateMessage state={state === 'unavailable' ? 'unavailable' : 'missing'} pharmacy={null} />;
  }

  const lang: Lang = info.form.languages.includes(locale) ? locale : 'en';

  const start = async () => {
    const found: Partial<Record<FieldName, MessageKey>> = {};
    if (!firstName.trim()) found.firstName = 'error.fieldRequired';
    if (!lastName.trim()) found.lastName = 'error.fieldRequired';
    const iso = isoFromParts(dob);
    if (!iso) found.dob = 'form.dobInvalid';
    if (!phone.trim() && !email.trim()) found.contact = 'form.contactRequired';
    else if (email.trim() && !isEmail(email.trim())) found.contact = 'error.emailInvalid';
    else if (phone.trim() && !isUsablePhone(phone.trim())) found.contact = 'error.phoneInvalid';
    setErrors(found);
    setRefusal(null);
    if (Object.keys(found).length > 0 || !iso) {
      setTimeout(() => summary.current?.focus(), 0);
      return;
    }
    if (info.spamCheck && !turnstile) {
      setRefusal('form.spamFailed');
      setTimeout(() => summary.current?.focus(), 0);
      return;
    }
    setBusy(true);
    try {
      const started = await formsApi.startPublic({
        publicToken,
        ...(turnstile ? { turnstile } : {}),
        ...(website ? { website } : {}),
        identity: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dob: iso,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
        },
        language: lang,
      });
      // The form runs on its own link from here, resumed with this session.
      rememberSession(started.token, started.session.secret);
      navigate(`/f/${started.token}`, { replace: true });
    } catch (error) {
      if (error instanceof FormApiError && error.code === 'identity_invalid') {
        const fields = Array.isArray(error.extra.fields) ? (error.extra.fields as string[]) : [];
        setErrors({
          ...(fields.includes('firstName') ? { firstName: 'error.fieldRequired' } : {}),
          ...(fields.includes('lastName') ? { lastName: 'error.fieldRequired' } : {}),
          ...(fields.includes('dob') ? { dob: 'form.dobInvalid' } : {}),
          ...(fields.includes('phone') ? { contact: 'error.phoneInvalid' } : {}),
          ...(fields.includes('email') ? { contact: 'error.emailInvalid' } : {}),
        });
      } else if (error instanceof FormApiError && error.code === 'spam_check_failed') {
        setRefusal('form.spamFailed');
      } else if (error instanceof FormApiError && error.code === 'daily_cap') {
        setRefusal('form.dailyCap');
      } else if (error instanceof FormApiError && error.code === 'not_accepted') {
        setRefusal('form.notAccepted');
      } else if (error instanceof FormApiError && error.status === 0) {
        setRefusal('error.offline');
      } else {
        setRefusal('error.unknown');
      }
      setTimeout(() => summary.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  };

  const fieldErrors = Object.entries(errors) as [FieldName, MessageKey][];

  return (
    <form
      noValidate
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        void start();
      }}
    >
      <div className="space-y-2">
        <h1 ref={heading} tabIndex={-1} className={HEADING}>
          {textIn(info.form.title, lang)}
        </h1>
        {info.form.intro && <p className="whitespace-pre-line text-lg">{textIn(info.form.intro, lang)}</p>}
        <p className="text-lg text-foreground">{t('form.publicIntro')}</p>
      </div>

      {(fieldErrors.length > 0 || refusal) && (
        <div ref={summary} tabIndex={-1} className="focus:outline-none">
          <Alert>
            {refusal ? t(refusal) : t('error.summary')}
            {!refusal && (
              <ul className="mt-1 list-disc pl-5">
                {fieldErrors.map(([name, key]) => (
                  <li key={name}>{t(key)}</li>
                ))}
              </ul>
            )}
          </Alert>
        </div>
      )}

      <Field id="pf-first" label={t('details.firstName')} required requiredLabel={t('details.required')} error={errors.firstName && t(errors.firstName)}>
        {(props) => (
          <Input {...props} autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        )}
      </Field>
      <Field id="pf-last" label={t('details.lastName')} required requiredLabel={t('details.required')} error={errors.lastName && t(errors.lastName)}>
        {(props) => (
          <Input {...props} autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        )}
      </Field>
      <DateOfBirth
        value={dob}
        onChange={setDob}
        error={errors.dob ? t(errors.dob) : null}
        legend={t('details.dob')}
        hint={t('form.dobHint')}
      />
      <div className="space-y-4">
        <p id="pf-contact-hint" className="text-base text-muted-foreground">
          {t('form.contactHint')}
        </p>
        <Field id="pf-phone" label={t('details.phone')} error={errors.contact && !email.trim() ? t(errors.contact) : undefined}>
          {(props) => (
            <Input
              {...props}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              aria-describedby={[props['aria-describedby'], 'pf-contact-hint'].filter(Boolean).join(' ')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field id="pf-email" label={t('details.email')} error={errors.contact && email.trim() ? t(errors.contact) : undefined}>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              aria-describedby={[props['aria-describedby'], 'pf-contact-hint'].filter(Boolean).join(' ')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
      </div>

      {/* For bots only: people never see it, and a filled one is refused. */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="pf-website">Website</label>
        <input
          id="pf-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {info.spamCheck && (
        <div className="space-y-2">
          <p className="text-base font-medium text-foreground">{t('form.spamCheck')}</p>
          <Turnstile siteKey={info.spamCheck} language={lang} onToken={setTurnstile} />
        </div>
      )}

      <Button type="submit" loading={busy}>
        {t('form.start')}
      </Button>
    </form>
  );
}
